/**
 * TanStack Query Client Configuration
 * Centralized configuration for React Query with default options
 */

import { QueryClient } from '@tanstack/react-query'
import { ApiClientError } from './client'
import { logger } from '@shared/lib/utils/logger'

// ============================================================================
// Query Client Configuration
// ============================================================================

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Stale time: how long data is considered fresh (5 minutes)
      staleTime: 1000 * 60 * 5,

      // Cache time: how long inactive data stays in cache (10 minutes)
      gcTime: 1000 * 60 * 10,

      // Retry logic for failed queries
      retry: (failureCount, error) => {
        // Don't retry on 4xx errors (client errors)
        if (error instanceof ApiClientError && error.statusCode) {
          if (error.statusCode >= 400 && error.statusCode < 500) {
            return false
          }
        }

        // Retry up to 2 times for other errors
        return failureCount < 2
      },

      // Retry delay with exponential backoff
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),

      // Refetch on window focus for critical data
      refetchOnWindowFocus: false,

      // Refetch on reconnect
      refetchOnReconnect: true,

      // Refetch on mount if data is stale
      refetchOnMount: true,
    },
    mutations: {
      // Retry logic for mutations
      retry: false, // Don't retry mutations by default

      // Error handling for mutations
      onError: (error) => {
        // Log error for debugging
        logger.error('Mutation error:', error)
      },
    },
  },
})
